<template>

  <ArticleLayout class="BlogPost"
    :backLink="{ path: context.indexPagePath, text: localeStrings.returnToIndex.blogPost }"
    :pageContent="content"
    v-bind="{ segment, relatedItemsContent }"
    showFooterRelatedItems
  >

    <template v-slot:header>
      <p v-if="content.category" class="category">{{ content.category.title }}</p>
      <ArticleHeading class="heading">{{ content.title }}</ArticleHeading>
      <p v-if="content.author" class="author">{{ localeStrings.byAuthor.replace('{author}', content.author) }}</p>
    </template>

    <template v-slot:body="{ pageCta }">
      <SanityImage v-if="content.image" class="image" :asset="content.image.asset" :alt="content.image.alt" />
      <ArticleOutline v-if="content.body" :blocks="content.body" v-bind="{ headingIds }" class="articleOutline" />
      <ArticleBody class="body" :blocks="content.body" v-bind="{ headingIds, pageCta }" />
    </template>

  </ArticleLayout>

</template>

<script>
import { extraContentQueryFragment } from './article/ArticleLayout.vue'
import { articlePreviewQueryFragment } from './shared/ArticlePreview.vue'

export const contentQuery = `
  *[_id == $pageId][0]{
    _type,
    locale,
    title,
    author,
    category->{ _id, title, slug, segment },
    image,
    body,
    "categoryRelatedResourceLinks":
      *[
        _type == ^._type &&
        locale == ^.locale &&
        _id != ^._id &&
        (defined(category) && category._ref == ^.category._ref) &&
        defined(slug)
      ]
      | order(date desc)
      [0..3]
      ${articlePreviewQueryFragment},
    "relatedResourceLinks":
      *[
        _type == ^._type &&
        locale == ^.locale &&
        _id != ^._id &&
        defined(slug)
      ]
      | score(category._ref == ^.category._ref)
      | order(_score desc, date desc)
      [0..3]
      ${articlePreviewQueryFragment},
    ${extraContentQueryFragment}
  }
`
</script>

<script setup>
import { provide } from 'vue'
import strings from './strings.json'

import ArticleBody from './article/ArticleBody.vue'
import ArticleHeading from './article/ArticleHeading.vue'
import ArticleLayout from './article/ArticleLayout.vue'
import ArticleOutline from './article/ArticleOutline.vue'

import { getCategoryRelatedItemsContent, genIdsForHeadings } from './lib/article-util'

const { content, context } = defineProps({ content: Object, context: Object })

const localeStrings = strings[content.locale]

provide('locale', content.locale)
provide('localeStrings', localeStrings)
provide('context', context)

const segment = content.category?.segment ?? localeStrings.defaultSegment
const relatedItemsContent = getCategoryRelatedItemsContent(content, context.indexPagePath)
const headingIds = genIdsForHeadings(content.body)
</script>

<style scoped>

.category {
  font-weight: 400;
  font-size: var(--font-size-2);
  line-height: 1.5;
  color: var(--text-color-black);
}

.author {
  font-weight: 600;
  font-size: var(--font-size-2);
  color: var(--text-color-black);
}

.category + .heading {
  margin-top: 0.25rem;
}

.heading + .author,
.heading + .date {
  margin-top: var(--spacing-6);
}

.author + .date {
  margin-top: 0.25rem;
}

.image {
  margin-top: var(--spacing-11);
  border-radius: var(--border-radius);
}

.articleOutline {
  margin-top: var(--spacing-8);
}

.body {
  margin-top: var(--spacing-11);
}
</style>
